import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Search, Filter } from "lucide-react";
import "./HRApplicationManagementPage.css";

import { getApplications } from "../../services/jobService";

import API from "../../services/api";

const STATUS_OPTIONS = [
  "APPLIED",
  "SHORTLISTED",
  "INTERVIEW_COMPLETED",
  "SELECTED",
  "OFFER_RELEASED",
  "REJECTED",
];

const LOCKED_STATUSES = new Set(["SELECTED", "OFFER_RELEASED", "REJECTED"]);

const formatStatusLabel = (status) => {
  return String(status || "-").replace(/_/g, " ");
};

const getStatusClassName = (status) => {
  return String(status || "")
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
};

const formatAppliedDate = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return parsedDate.toLocaleDateString("en-GB");
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return value;
};

const formatDateValue = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return parsedDate.toLocaleDateString("en-GB");
};

const isFilterableJobTitle = (title) => {
  const normalizedTitle = String(title || "").trim().toLowerCase();
  return normalizedTitle && normalizedTitle !== "archived role";
};

const HRApplicationManagementPage = () => {
  const applicationsPerPage = 10;

  const [query, setQuery] = useState("");
  const [jobFilter, setJobFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [referredFilter, setReferredFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [activeCandidateId, setActiveCandidateId] = useState(null);
  const [detailStatus, setDetailStatus] = useState("");
  const [isStatusChanged, setIsStatusChanged] = useState(false);

  const [applications, setApplications] = useState([]);

  // ================= FETCH =================
  const fetchApplications = useCallback(async () => {
    try {
      const response = await getApplications(
        referredFilter ? { referred: referredFilter } : {},
      );
      console.log(response);
      const visibleApplications = Array.isArray(response.data)
        ? response.data.filter((app) => isFilterableJobTitle(app.jobTitle))
        : [];
      setApplications(visibleApplications);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      setApplications([]);
    }
  }, [referredFilter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // ================= OPTIONS =================
  const jobOptions = useMemo(
    () => [
      "All",
      ...new Set(
        applications
          .map((app) => app.jobTitle)
          .filter(isFilterableJobTitle),
      ),
    ],
    [applications],
  );

  const statusOptions = [
    "Select the Status",
    "APPLIED",
    "SHORTLISTED",
    "INTERVIEW_COMPLETED",
    "SELECTED",
    "REJECTED",
    "OFFER_RELEASED",
  ];

  const referredOptions = [
    { label: "Is Referred", value: "" },
    { label: "True", value: "true" },
    { label: "False", value: "false" },
  ];

  // ================= FILTER =================
  const filteredApplications = useMemo(() => {
    return applications.filter((app) => {
      const matchesQuery =
        (app.fullName || "").toLowerCase().includes(query.toLowerCase()) ||
        (app.jobTitle || "").toLowerCase().includes(query.toLowerCase());

      const matchesJob = jobFilter === "All" || app.jobTitle === jobFilter;
      const matchesStatus =
        statusFilter === "All" || app.status === statusFilter;

      return matchesQuery && matchesJob && matchesStatus;
    });
  }, [applications, jobFilter, query, statusFilter]);

  const totalPages = useMemo(
    () =>
      Math.max(Math.ceil(filteredApplications.length / applicationsPerPage), 1),
    [filteredApplications.length],
  );

  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * applicationsPerPage;
    return filteredApplications.slice(
      startIndex,
      startIndex + applicationsPerPage,
    );
  }, [applicationsPerPage, currentPage, filteredApplications]);

  useEffect(() => {
    setCurrentPage(1);
  }, [jobFilter, query, referredFilter, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // ================= ACTIVE CANDIDATE =================
  const activeCandidate = useMemo(
    () =>
      applications.find((app) => app.applicationId === activeCandidateId) ||
      null,
    [activeCandidateId, applications],
  );

  useEffect(() => {
    if (activeCandidate) {
      setDetailStatus(activeCandidate.status);
    } else {
      setDetailStatus("");
    }
  }, [activeCandidate]);

  // ================= NEW STATUS UPDATE FUNCTION =================
  const updateStatus = async (applicationId, status) => {
    try {
      const res = await API.put(`/applications/${applicationId}/status`, {
        status,
      });

      if (res.data.success) {
        setApplications((prev) =>
          prev.map((app) =>
            app.applicationId === applicationId ? { ...app, status } : app,
          ),
        );
      }
    } catch (error) {
      console.error("Status update failed:", error);

      alert(error?.response?.data?.message || "Failed to update status");
    }
  };

  // ================= SAVE STATUS =================
  const handleSaveStatus = async () => {
    try {
      console.log("Updating status:", detailStatus);
      console.log("Application ID:", activeCandidate.applicationId);

      if (!activeCandidate?.applicationId) {
        console.error("Application ID missing");
        return;
      }

      await updateStatus(activeCandidate.applicationId, detailStatus);

      await fetchApplications();

      setIsStatusChanged(false);
    } catch (error) {
      console.error("Status update failed:", error);
    }
  };

  // ================= DETAIL PAGE =================
  if (activeCandidate) {
    return (
      <article className="hr-page-card">
        <div className="candidate-detail-title-row">
          <div className="hr-page-title">
            <ClipboardList size={20} />
            <h2>Candidate Application Details</h2>
          </div>
          <button
            type="button"
            className="action-btn detail-back-btn"
            onClick={() => setActiveCandidateId(null)}
          >
            Back to Applications
          </button>
        </div>

        <section className="candidate-details-card no-top-margin">
          <h3>Candidate Full Details</h3>

          <div className="candidate-details-grid">
            <div>
              <span>Name</span>
              <strong>{activeCandidate.fullName}</strong>
            </div>
            <div>
              <span>Applied Role</span>
              <strong>{activeCandidate.jobTitle}</strong>
            </div>

            <div>
              <span>Current Status</span>
              <span
                className={`status-pill detail-status-pill status-${getStatusClassName(
                  activeCandidate.status,
                )}`}
              >
                {formatStatusLabel(activeCandidate.status)}
              </span>
            </div>
               <div>
              <span>Job Code</span>
              <strong>{activeCandidate.jobCode}</strong>
            </div>
          </div>

          <div className="candidate-detail-section">
            <h4>My Documents</h4>
            <div className="candidate-details-grid">
              <div>
                <span>Resume</span>
                <strong>
                  {activeCandidate.resumeUrl ? "Available" : "-"}
                </strong>
              </div>
              <div>
                <span>Applied On</span>
                <strong>{formatAppliedDate(activeCandidate.createdAt)}</strong>
              </div>
            </div>
          </div>

          <div className="candidate-detail-section">
            <h4>Profile Information</h4>
            <div className="candidate-details-grid">
              <div>
                <span>Title</span>
                <strong>{formatValue(activeCandidate.title)}</strong>
              </div>
              <div>
                <span>First Name</span>
                <strong>{formatValue(activeCandidate.firstName)}</strong>
              </div>
              <div>
                <span>Middle Name</span>
                <strong>{formatValue(activeCandidate.middleName)}</strong>
              </div>
              <div>
                <span>Last Name</span>
                <strong>{formatValue(activeCandidate.lastName)}</strong>
              </div>
              <div>
                <span>Date of Birth</span>
                <strong>{formatDateValue(activeCandidate.dateOfBirth)}</strong>
              </div>
              <div>
                <span>Primary Phone Number</span>
                <strong>{formatValue(activeCandidate.phone)}</strong>
              </div>
              <div>
                <span>Primary Email ID</span>
                <strong>{formatValue(activeCandidate.email)}</strong>
              </div>
              <div>
                <span>Secondary Phone</span>
                <strong>{formatValue(activeCandidate.secondaryPhone)}</strong>
              </div>
              <div>
                <span>Total Experience</span>
                <strong>{formatValue(activeCandidate.totalExperience)}</strong>
              </div>
              <div>
                <span>Country of Residence</span>
                <strong>{formatValue(activeCandidate.currentCountry)}</strong>
              </div>
              <div>
                <span>Address Line 1</span>
                <strong>{formatValue(activeCandidate.currentAddressLine1)}</strong>
              </div>
              <div>
                <span>Address Line 2</span>
                <strong>{formatValue(activeCandidate.currentAddressLine2)}</strong>
              </div>
              <div>
                <span>City</span>
                <strong>{formatValue(activeCandidate.currentCity)}</strong>
              </div>
              <div>
                <span>State / Province / Region</span>
                <strong>{formatValue(activeCandidate.currentState)}</strong>
              </div>
              <div>
                <span>Zip Code</span>
                <strong>{formatValue(activeCandidate.currentZipCode)}</strong>
              </div>
            </div>
          </div>

          <div className="candidate-detail-section">
            <h4>Education</h4>
            <div className="candidate-details-grid">
              <div>
                <span>Is this Higher Education?</span>
                <strong>{formatValue(activeCandidate.isHigherEducation)}</strong>
              </div>
              <div>
                <span>Qualification</span>
                <strong>{formatValue(activeCandidate.qualification)}</strong>
              </div>
              <div>
                <span>Specialization</span>
                <strong>{formatValue(activeCandidate.specialization)}</strong>
              </div>
              <div>
                <span>Education Type</span>
                <strong>{formatValue(activeCandidate.educationType)}</strong>
              </div>
              <div>
                <span>College / Institute Name</span>
                <strong>{formatValue(activeCandidate.collegeInstituteName)}</strong>
              </div>
              <div>
                <span>University</span>
                <strong>{formatValue(activeCandidate.universityName)}</strong>
              </div>
              <div>
                <span>From Date</span>
                <strong>{formatDateValue(activeCandidate.educationFromDate)}</strong>
              </div>
              <div>
                <span>End Date</span>
                <strong>{formatDateValue(activeCandidate.educationEndDate)}</strong>
              </div>
            </div>

            {Array.isArray(activeCandidate.educations) &&
              activeCandidate.educations.slice(1).length > 0 && (
                <div className="candidate-collection-list">
                  <span className="candidate-collection-title">
                    Additional Education Entries
                  </span>
                  {activeCandidate.educations
                    .slice(1)
                    .map((education, index) => (
                    <div
                      className="candidate-collection-card"
                      key={`education-${index}`}
                    >
                      <strong>Education {index + 1}</strong>
                      <div className="candidate-collection-grid">
                        <div>
                          <span>Is this Higher Education?</span>
                          <strong>
                            {formatValue(education.isHigherEducation)}
                          </strong>
                        </div>
                        <div>
                          <span>Qualification</span>
                          <strong>{formatValue(education.qualification)}</strong>
                        </div>
                        <div>
                          <span>Specialization</span>
                          <strong>{formatValue(education.specialization)}</strong>
                        </div>
                        <div>
                          <span>Education Type</span>
                          <strong>{formatValue(education.educationType)}</strong>
                        </div>
                        <div>
                          <span>College / Institute Name</span>
                          <strong>
                            {formatValue(education.collegeInstituteName)}
                          </strong>
                        </div>
                        <div>
                          <span>University Name</span>
                          <strong>{formatValue(education.universityName)}</strong>
                        </div>
                        <div>
                          <span>From Date</span>
                          <strong>
                            {formatDateValue(education.educationFromDate)}
                          </strong>
                        </div>
                        <div>
                          <span>End Date</span>
                          <strong>
                            {formatDateValue(education.educationEndDate)}
                          </strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>

          <div className="candidate-detail-section">
            <h4>Job-Specific Information</h4>
            <div className="candidate-details-grid">
              <div>
                <span>Skills</span>
                <strong>{formatValue(activeCandidate.skills)}</strong>
              </div>
              <div>
                <span>Other Skill Set</span>
                <strong>{formatValue(activeCandidate.otherSkillSet)}</strong>
              </div>
              <div>
                <span>LinkedIn</span>
                <strong>{formatValue(activeCandidate.linkedin)}</strong>
              </div>
              <div>
                <span>Portfolio / GitHub</span>
                <strong>{formatValue(activeCandidate.portfolio)}</strong>
              </div>
            </div>
          </div>

          {Array.isArray(activeCandidate.certifications) &&
            activeCandidate.certifications.length > 0 && (
              <div className="candidate-detail-section">
                <h4>Certifications</h4>
                <div className="candidate-collection-list">
                  {activeCandidate.certifications.map((certification, index) => (
                    <div
                      className="candidate-collection-card"
                      key={`certification-${index}`}
                    >
                      <strong>Certification {index + 1}</strong>
                      <div className="candidate-collection-grid">
                        <div>
                          <span>Certification Name</span>
                          <strong>
                            {formatValue(certification.certificationName)}
                          </strong>
                        </div>
                        <div>
                          <span>Institution</span>
                          <strong>{formatValue(certification.institution)}</strong>
                        </div>
                        <div>
                          <span>Effective Date</span>
                          <strong>
                            {formatDateValue(certification.effectiveDate)}
                          </strong>
                        </div>
                        <div>
                          <span>Expiration Date</span>
                          <strong>
                            {formatDateValue(certification.expirationDate)}
                          </strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          <div className="detail-actions-row">
            <select
              value={detailStatus}
              disabled={LOCKED_STATUSES.has(activeCandidate.status)}
              onChange={(e) => {
                setDetailStatus(e.target.value);
                setIsStatusChanged(true);
              }}
            >
              <option value="">Update Status</option>

              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {formatStatusLabel(status)}
                </option>
              ))}
            </select>

            {isStatusChanged && (
              <button
                className="action-btn save-btn"
                style={{ color: "black" }}
                onClick={handleSaveStatus}
              >
                Save
              </button>
            )}

            <a
              className="action-btn resume-btn"
              href={activeCandidate.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Resume
            </a>
          </div>
        </section>
      </article>
    );
  }

  // ================= LIST PAGE =================
  return (
    <article className="hr-page-card">
      <div className="hr-page-title">
        <ClipboardList size={20} />
        <h2>Application Management</h2>
      </div>

      <div className="app-filters">
        <div className="search-wrap">
          <Search size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
            placeholder="Search candidate name or job title"
          />
        </div>

        <div className="filter-wrap">
          <Filter size={16} />
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
          >
            {jobOptions.map((job) => (
              <option key={job} value={job}>
                {job === "All" ? "All Jobs" : job}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-wrap">
          <Filter size={16} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-wrap">
          <Filter size={16} />
          <select
            value={referredFilter}
            onChange={(e) => setReferredFilter(e.target.value)}
          >
            {referredOptions.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="applications-table">
        <div className="table-head">
          <span>Candidate</span>
          <span>Job</span>
        </div>

        {paginatedApplications.map((app) => (
          <div className="table-row" key={app.applicationId}>
            <button
              type="button"
              className="candidate-open-btn"
              onClick={() => setActiveCandidateId(app.applicationId)}
            >
              <p className="candidate-name">Name: {app.fullName}</p>
              <p className="candidate-email">Email: {app.email}</p>
              <p className="candidate-detail-line">Phone: {app.phone}</p>
              <p className="candidate-detail-line">Location: {app.location}</p>
              <p className="candidate-detail-line">
                Referred By: {app.employeeName?.trim() || "null"}
              </p>
              <p className="candidate-detail-line candidate-status-line">
                Current Status:
                <span
                  className={`status-pill status-${getStatusClassName(
                    app.status,
                  )}`}
                >
                  {formatStatusLabel(app.status || "APPLIED")}
                </span>
              </p>
            </button>
            {/* <div className="job-applied-cell">
              <strong>{app.jobTitle}</strong>
              <p>Applied On: {app.createdAt}</p>
            </div> */}
            <div className="job-applied-cell">
              <strong>{app.jobTitle}</strong>
              <p>Applied On: {formatAppliedDate(app.createdAt)}</p>
            </div>
          </div>
        ))}

        {filteredApplications.length === 0 && (
          <div className="empty-state">
            No applications found for selected criteria.
          </div>
        )}
      </div>

      {filteredApplications.length > 0 && (
        <div className="application-pagination">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <span className="pagination-status">
            Page {currentPage} of {totalPages}
          </span>

          <button
            type="button"
            className="pagination-btn"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </article>
  );
};

export default HRApplicationManagementPage;
 
